import React, { useEffect, useState } from 'react';
import axios from '../axios-config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Receipt, CheckCircle2, AlertCircle, CreditCard, Lock, Download, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function SupplierSuccessFees() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryInvoice, setSummaryInvoice] = useState(null); // invoice shown on summary screen
  const [payingNow, setPayingNow] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/supplier/success-fee-invoices`);
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // STEP 1 — open the Invoice Summary screen (does NOT open Razorpay yet)
  const openSummary = (inv) => setSummaryInvoice(inv);
  const closeSummary = () => { if (!payingNow) setSummaryInvoice(null); };

  // STEP 2 — Pay Now: create order → open Razorpay
  const payNow = async () => {
    if (!summaryInvoice) return;
    setPayingNow(true);
    try {
      const { data } = await axios.post(`${API}/payments/success-fee/order`, { invoice_id: summaryInvoice.id });

      // Payments not yet enabled (test/disabled mode) — fall back to existing mock-pay so dev flow keeps working
      if (!data?.enabled) {
        await axios.post(`${API}/supplier/success-fee-invoices/${summaryInvoice.id}/mark-paid`, { payment_method: 'razorpay_test' });
        toast.success('Payment recorded (test mode) — PO unlocked');
        setSummaryInvoice(null);
        await load();
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Failed to load Razorpay'); return; }

      const options = {
        key: data.razorpay_key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        order_id: data.order.id,
        name: 'Plus Ultra Innovation',
        description: `Success Fee — ${summaryInvoice.tender_title || summaryInvoice.tender_id}`,
        handler: async () => {
          // Verification + final state-change happens on the webhook (`payment.captured`).
          // We poll the invoice once to reflect status promptly in the UI.
          toast.success('Payment received — finalising…');
          setTimeout(load, 1500);
          setTimeout(load, 4000);
          setSummaryInvoice(null);
        },
        modal: { ondismiss: () => toast.info('Payment cancelled. Invoice remains pending.') },
        theme: { color: '#0a2540' },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start payment');
    } finally { setPayingNow(false); }
  };

  const downloadPO = async (tenderId) => {
    try {
      const { data } = await axios.get(`${API}/tenders/${tenderId}/po-download`);
      toast.success(`PO ${data.purchase_order?.po_number} unlocked`);
      if (data.purchase_order?.po_document?.url) {
        window.open(data.purchase_order.po_document.url, '_blank');
      } else {
        toast.info('PO data retrieved (no attached file)');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'PO download failed';
      toast.error(detail);
    }
  };

  const pending = invoices.filter(i => i.status === 'pending');
  const paid = invoices.filter(i => i.status === 'paid');

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="bg-navy text-white sticky top-0 z-30">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/supplier-dashboard')} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="success-fees-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </button>
          <div className="font-bold text-sm sm:text-base inline-flex items-center"><Receipt className="h-4 w-4 mr-1.5 text-gold" /> Platform Success Fees</div>
          <div className="w-12" />
        </nav>
      </header>

      <div className="container mx-auto px-4 py-5 max-w-3xl space-y-5">
        <div className="bg-card border border-border rounded-lg p-4 text-xs text-muted-foreground" data-testid="success-fees-intro">
          When a tender is awarded to you, a Platform Success Fee invoice is auto-generated. <strong>Pay the invoice to unlock your Purchase Order download.</strong>
        </div>

        {loading ? (
          <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-border rounded-lg" data-testid="no-invoices-empty">
            <Receipt className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No success fee invoices yet. They appear here once a tender is awarded to you.</p>
          </div>
        ) : (
          <>
            {pending.length > 0 && (
              <section>
                <h3 className="text-xs font-bold text-orange mb-2 uppercase tracking-wide" data-testid="pending-invoices-heading">
                  Pending Payments ({pending.length}) — PO Locked
                </h3>
                <div className="space-y-3">
                  {pending.map(inv => (
                    <InvoiceCard key={inv.id} inv={inv} onReview={() => openSummary(inv)} testidBase={`pending-inv-${inv.id}`} />
                  ))}
                </div>
              </section>
            )}
            {paid.length > 0 && (
              <section>
                <h3 className="text-xs font-bold text-green-700 mb-2 uppercase tracking-wide mt-6" data-testid="paid-invoices-heading">
                  Paid Invoices ({paid.length}) — PO Unlocked
                </h3>
                <div className="space-y-3">
                  {paid.map(inv => (
                    <InvoiceCard key={inv.id} inv={inv} paid onDownloadPO={() => downloadPO(inv.tender_id)} testidBase={`paid-inv-${inv.id}`} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {summaryInvoice && (
        <InvoiceSummaryModal inv={summaryInvoice} onClose={closeSummary} onPay={payNow} paying={payingNow} />
      )}
    </div>
  );
}

const InvoiceCard = ({ inv, onReview, onDownloadPO, paid, testidBase }) => (
  <div className={`border-2 rounded-lg p-4 ${paid ? 'border-green-300 bg-green-50/30' : 'border-orange/40 bg-orange/5'}`} data-testid={testidBase}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1">
        <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide">Invoice {inv.invoice_number}</div>
        <div className="text-sm font-bold text-navy mt-0.5" data-testid={`${testidBase}-title`}>{inv.tender_title}</div>
        <div className="text-[11px] text-muted-foreground">Tender ID: <span className="font-mono">{inv.tender_id}</span></div>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center ${paid ? 'bg-green-100 text-green-800' : 'bg-orange/20 text-orange'}`} data-testid={`${testidBase}-status`}>
        {paid ? <><CheckCircle2 className="h-3 w-3 mr-1" /> PAID</> : <><AlertCircle className="h-3 w-3 mr-1" /> PENDING</>}
      </span>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] mb-3">
      <Stat label="Contract Value" value={fmt(inv.contract_value)} testid={`${testidBase}-contract`} />
      <Stat label={`Success Fee (${inv.fee_percentage}%)`} value={fmt(inv.fee_amount)} testid={`${testidBase}-fee`} />
      <Stat label={`GST (${inv.gst_percentage}%)`} value={fmt(inv.gst_amount)} testid={`${testidBase}-gst`} />
      <Stat label="Total Payable" value={fmt(inv.total_amount)} highlight testid={`${testidBase}-total`} />
    </div>

    {paid ? (
      <button onClick={onDownloadPO} className="w-full px-4 py-2.5 rounded bg-green-600 text-white text-sm font-bold inline-flex items-center justify-center" data-testid={`${testidBase}-po-download-btn`}>
        <Download className="h-4 w-4 mr-2" /> Download Purchase Order
      </button>
    ) : (
      <div className="space-y-2">
        <div className="text-[11px] text-orange flex items-center"><Lock className="h-3 w-3 mr-1" /> Purchase Order is locked until payment is completed.</div>
        <button onClick={onReview} className="w-full px-4 py-3 rounded bg-orange text-white text-sm font-bold inline-flex items-center justify-center" data-testid={`${testidBase}-review-btn`}>
          <Receipt className="h-4 w-4 mr-2" /> Review Invoice & Pay
        </button>
      </div>
    )}
  </div>
);

const Stat = ({ label, value, highlight, testid }) => (
  <div className={`p-2 rounded ${highlight ? 'bg-navy text-white' : 'bg-card border border-border'}`} data-testid={testid}>
    <div className={`text-[9px] uppercase font-bold tracking-wide ${highlight ? 'text-gold' : 'text-muted-foreground'}`}>{label}</div>
    <div className={`text-xs font-bold ${highlight ? 'text-white' : 'text-navy'}`}>{value}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Invoice Summary Screen (modal) — shown before Razorpay opens
// ─────────────────────────────────────────────────────────────
const InvoiceSummaryModal = ({ inv, onClose, onPay, paying }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="invoice-summary-modal">
    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
      <div className="sticky top-0 bg-navy text-white px-5 py-3 flex items-center justify-between rounded-t-lg">
        <div className="font-bold text-sm inline-flex items-center"><Receipt className="h-4 w-4 mr-2 text-gold" /> Invoice Summary</div>
        <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="summary-close-btn"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-5 space-y-4">
        <Row label="Invoice Number" value={inv.invoice_number} testid="summary-invoice-no" />
        <Row label="Tender Reference" value={inv.tender_id} mono testid="summary-tender-ref" />
        <Row label="Tender Title" value={inv.tender_title} testid="summary-tender-title" />
        <div className="h-px bg-border" />
        <Row label="Award Value (Contract)" value={fmt(inv.contract_value)} testid="summary-award-value" />
        <Row label={`Success Fee @ ${inv.fee_percentage}%`} value={fmt(inv.fee_amount)} testid="summary-fee" />
        <Row label={`GST @ ${inv.gst_percentage}%`} value={fmt(inv.gst_amount)} testid="summary-gst" />
        <div className="h-px bg-border" />
        <div className="flex items-center justify-between bg-navy text-white px-4 py-3 rounded">
          <span className="text-xs uppercase font-bold tracking-wider text-gold">Total Amount Payable</span>
          <span className="text-lg font-bold" data-testid="summary-total">{fmt(inv.total_amount)}</span>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Payment is processed securely via Razorpay. Your Purchase Order will be unlocked automatically once payment is verified.
        </div>
        <button onClick={onPay} disabled={paying} className="w-full px-4 py-3 rounded bg-orange text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="summary-pay-now-btn">
          <CreditCard className="h-4 w-4 mr-2" /> {paying ? 'Opening Razorpay…' : `Pay Now — ${fmt(inv.total_amount)}`}
        </button>
        <button onClick={onClose} disabled={paying} className="w-full px-4 py-2 rounded border border-border text-sm text-navy" data-testid="summary-cancel-btn">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const Row = ({ label, value, mono, testid }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-medium text-navy text-right ${mono ? 'font-mono text-xs' : ''}`} data-testid={testid}>{value}</span>
  </div>
);
